/**
 * ControlPanel API (`/features` `/permission-groups`) クライアント (権限管理 SBI-8)。
 *
 * - `GET /features`: 権限グループに付与できる機能のカタログ (チェックボックスの選択肢と表示名)。
 * - `/permission-groups`: 権限グループの一覧 / 作成 / 更新 / 削除。
 *
 * ⚠️ 最終的な認可は常にバックエンド (403)。画面の出し分けは利便性のため。
 */
import { adminRequest, encodeSegment, type AdminClientOptions } from './adminRequest';
import type {
  PermissionFeatureListResponse,
  PermissionGroupCreateRequest,
  PermissionGroupDeleteResponse,
  PermissionGroupListResponse,
  PermissionGroupSaveResult,
  PermissionGroupUpdateRequest,
} from './types';

export type PermissionGroupsClientOptions = AdminClientOptions;

export class PermissionGroupsClient {
  private readonly options: PermissionGroupsClientOptions;

  constructor(options: PermissionGroupsClientOptions) {
    this.options = options;
  }

  /** GET /features (機能カタログ。定義順がそのまま表示順) */
  async listFeatures(): Promise<PermissionFeatureListResponse> {
    return adminRequest<PermissionFeatureListResponse>(this.options, 'GET', 'features');
  }

  /** GET /permission-groups (名称順) */
  async list(): Promise<PermissionGroupListResponse> {
    return adminRequest<PermissionGroupListResponse>(this.options, 'GET', 'permission-groups');
  }

  /** POST /permission-groups */
  async create(payload: PermissionGroupCreateRequest): Promise<PermissionGroupSaveResult> {
    return adminRequest<PermissionGroupSaveResult>(
      this.options,
      'POST',
      'permission-groups',
      payload,
    );
  }

  /** PUT /permission-groups/{group_id} (指定した項目だけ更新。`features` は置き換え) */
  async update(
    groupId: string,
    payload: PermissionGroupUpdateRequest,
  ): Promise<PermissionGroupSaveResult> {
    return adminRequest<PermissionGroupSaveResult>(
      this.options,
      'PUT',
      `permission-groups/${encodeSegment(groupId)}`,
      payload,
    );
  }

  /** DELETE /permission-groups/{group_id} (所属ユーザーがいる / 最後の管理権限グループは 409) */
  async remove(groupId: string): Promise<PermissionGroupDeleteResponse> {
    return adminRequest<PermissionGroupDeleteResponse>(
      this.options,
      'DELETE',
      `permission-groups/${encodeSegment(groupId)}`,
    );
  }
}
